import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import {
  useGetManualServicesQuery,
  useDeleteManualServiceMutation,
} from '../../../store/api/manualServicesApi';
import type { ManualService } from '../../../types/api';
import { formatDate } from '../../../utils/dateFormatter';

export default function ManualServicesListPage() {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [visible, setVisible] = useState<boolean | null>(null);
  const [maintenance, setMaintenance] = useState<boolean | null>(null);

  const { data, isLoading, error } = useGetManualServicesQuery({
    page,
    limit,
    visible: visible ?? undefined,
    maintenance: maintenance ?? undefined,
  });
  const [deleteManualService, { isLoading: isDeleting }] = useDeleteManualServiceMutation();

  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; key: string | null }>({
    isOpen: false,
    key: null,
  });

  const services: ManualService[] = data?.data?.services || [];
  const pagination = data?.data?.pagination;

  const handleDelete = async (key: string) => {
    try {
      await deleteManualService(key).unwrap();
      toast.success(t('admin.manualServices.deleteSuccess') || 'Manual service deleted successfully');
      setDeleteDialog({ isOpen: false, key: null });
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to delete manual service');
    }
  };

  const clearFilters = () => {
    setVisible(null);
    setMaintenance(null);
    setPage(1);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.manualServices.error.title') || 'Error loading manual services'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header with animation */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent">
            {t('admin.manualServices.title') || 'Manual Services'}
          </h1>
          <div className="flex items-center gap-3">
            {pagination && (
              <div className="bg-brand-accent/20 border border-brand-accent/50 rounded-lg px-3 sm:px-4 py-2 w-fit">
                <span className="text-xs sm:text-sm font-medium text-neutral-medium">
                  {t('admin.manualServices.total') || 'Total Services'}:{' '}
                </span>
                <span className="text-base sm:text-lg font-heading font-bold text-brand-accent">
                  {pagination.total}
                </span>
              </div>
            )}
            <Link
              to="/admin/manual-services/new"
              className="px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
            >
              {t('admin.manualServices.addNew') || 'Add New Service'}
            </Link>
          </div>
        </div>
        <p className="text-neutral-medium text-sm sm:text-base">
          {t('admin.manualServices.subtitle') || 'Manage manual services that appear in the bot menu'}
        </p>
      </div>

      {/* Filters */}
      <div
        className="mb-6 bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 border border-neutral-dark/50"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
          }}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                {t('admin.manualServices.filters.visible') || 'Visible'}
              </label>
              <select
                value={visible === null ? '' : visible.toString()}
                onChange={(e) => setVisible(e.target.value === '' ? null : e.target.value === 'true')}
                className="w-full px-3 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
              >
                <option value="">{t('admin.manualServices.filters.all') || 'All'}</option>
                <option value="true">{t('admin.manualServices.filters.visible') || 'Visible'}</option>
                <option value="false">{t('admin.manualServices.filters.hidden') || 'Hidden'}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                {t('admin.manualServices.filters.maintenance') || 'Maintenance'}
              </label>
              <select
                value={maintenance === null ? '' : maintenance.toString()}
                onChange={(e) => setMaintenance(e.target.value === '' ? null : e.target.value === 'true')}
                className="w-full px-3 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
              >
                <option value="">{t('admin.manualServices.filters.all') || 'All'}</option>
                <option value="true">{t('admin.manualServices.filters.inMaintenance') || 'In Maintenance'}</option>
                <option value="false">{t('admin.manualServices.filters.active') || 'Active'}</option>
              </select>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={clearFilters}
              className="w-full sm:w-auto px-6 py-2 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
            >
              {t('admin.manualServices.filters.clear') || 'Clear Filters'}
            </button>
          </div>
        </form>
      </div>

      {/* Services Table (Desktop) */}
      {services.length === 0 ? (
        <div
          className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-12 border border-neutral-dark/50 text-center"
          style={{
            animation: 'fadeIn 0.5s ease-out 0.2s both',
          }}
        >
          <p className="text-neutral-medium text-lg mb-4">
            {t('admin.manualServices.empty') || 'No manual services found'}
          </p>
          <Link
            to="/admin/manual-services/new"
            className="inline-block px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            {t('admin.manualServices.addNew') || 'Add New Service'}
          </Link>
        </div>
      ) : (
        <>
          <div
            className="hidden lg:block bg-neutral-dark/50 backdrop-blur-lg rounded-lg border border-neutral-dark/50 overflow-hidden transition-all duration-300 hover:border-brand-accent/30"
            style={{
              animation: 'slideUp 0.5s ease-out 0.1s both',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-dark/80 border-b border-neutral-medium">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.manualServices.table.key') || 'Key'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.manualServices.table.title') || 'Title'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.manualServices.table.status') || 'Status'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.manualServices.table.created') || 'Created'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.manualServices.table.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-medium">
                  {services.map((service, index) => (
                    <tr
                      key={service._id}
                      className="hover:bg-neutral-dark/50 transition-all duration-200 hover:shadow-lg hover:shadow-brand-accent/5"
                      style={{
                        animation: `fadeIn 0.4s ease-out ${index * 30}ms both`,
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-light text-sm font-mono">
                        {service.key}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-light text-sm">
                        <div>
                          <div className="font-medium">{service.title}</div>
                          {service.titleEn && (
                            <div className="text-xs text-neutral-medium mt-1">{service.titleEn}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {service.visible ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-green-500/20 text-green-400 border border-green-500/50">
                              {t('admin.manualServices.status.visible') || 'Visible'}
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-500/20 text-gray-400 border border-gray-500/50">
                              {t('admin.manualServices.status.hidden') || 'Hidden'}
                            </span>
                          )}
                          {service.maintenance && (
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">
                              {t('admin.manualServices.status.maintenance') || 'Maintenance'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-medium text-sm">
                        {formatDate(service.createdAt, 'PP', currentLanguage)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/manual-services/${service.key}`}
                            className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1"
                          >
                            {t('admin.manualServices.actions.view') || 'View'}
                            <svg
                              className="w-4 h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </Link>
                          <button
                            onClick={() => setDeleteDialog({ isOpen: true, key: service.key })}
                            className="text-red-400 hover:text-red-300 font-semibold transition-all duration-200 hover:underline"
                          >
                            {t('admin.manualServices.actions.delete') || 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Services Cards (Mobile) */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
            {services.map((service, index) => (
              <div
                key={service._id}
                className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 border border-neutral-dark/50 transition-all duration-200 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/5 animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-heading font-bold text-neutral-light font-mono">{service.key}</h3>
                  <Link
                    to={`/admin/manual-services/${service.key}`}
                    className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1 text-sm"
                  >
                    {t('admin.manualServices.actions.view') || 'View'}
                    <svg
                      className="w-4 h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <div className="font-medium text-neutral-light">{service.title}</div>
                    {service.titleEn && (
                      <div className="text-xs text-neutral-medium mt-1">{service.titleEn}</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {service.visible ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-green-500/20 text-green-400 border border-green-500/50">
                        {t('admin.manualServices.status.visible') || 'Visible'}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-500/20 text-gray-400 border border-gray-500/50">
                        {t('admin.manualServices.status.hidden') || 'Hidden'}
                      </span>
                    )}
                    {service.maintenance && (
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">
                        {t('admin.manualServices.status.maintenance') || 'Maintenance'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-medium font-medium">
                      {t('admin.manualServices.table.created') || 'Created'}:{' '}
                    </span>
                    <span className="text-neutral-light text-xs">
                      {formatDate(service.createdAt, 'PP', currentLanguage)}
                    </span>
                  </div>
                  <button
                    onClick={() => setDeleteDialog({ isOpen: true, key: service.key })}
                    className="w-full mt-2 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-all duration-200 text-sm"
                  >
                    {t('admin.manualServices.actions.delete') || 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div
              className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{
                animation: 'fadeIn 0.5s ease-out 0.3s both',
              }}
            >
              <div className="text-sm text-neutral-medium">
                {t('admin.manualServices.pagination.showing') || 'Showing'} {((page - 1) * limit) + 1} -{' '}
                {Math.min(page * limit, pagination.total)} {t('admin.manualServices.pagination.of') || 'of'}{' '}
                {pagination.total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                >
                  {t('admin.manualServices.pagination.previous') || 'Previous'}
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                >
                  {t('admin.manualServices.pagination.next') || 'Next'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, key: null })}
        onConfirm={() => {
          if (deleteDialog.key) {
            void handleDelete(deleteDialog.key);
          }
        }}
        title={t('admin.manualServices.deleteTitle') || 'Delete Manual Service'}
        message={
          t('admin.manualServices.deleteConfirm') ||
          'Are you sure you want to delete this manual service? This action cannot be undone.'
        }
        confirmText={t('common.confirm') || 'Confirm'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}



